// Firebase Configuration for Rare Canvas
// This enables cross-device data storage for submissions

import { initializeApp } from 'firebase/app';
import {
  getDatabase,
  ref,
  push,
  set,
  get,
  update,
  remove,
  onValue
} from 'firebase/database';

// Firebase configuration
// IMPORTANT: Replace these with your own Firebase project credentials
// Go to: https://console.firebase.google.com
// 1. Create a new project (or use existing)
// 2. Go to Project Settings > General > Your apps > Add app (Web)
// 3. Copy the config values here
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "YOUR_API_KEY",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "your-project.firebaseapp.com",
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL || "https://your-project-default-rtdb.firebaseio.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "your-project-id",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "your-project.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:123456789:web:abc123"
};

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return firebaseConfig.apiKey &&
         firebaseConfig.apiKey !== "YOUR_API_KEY" &&
         firebaseConfig.databaseURL &&
         firebaseConfig.databaseURL !== "https://your-project-default-rtdb.firebaseio.com";
};

// Initialize Firebase (only if configured)
let app = null;
let database = null;

if (isFirebaseConfigured()) {
  try {
    app = initializeApp(firebaseConfig);
    database = getDatabase(app);
    console.log('Firebase initialized successfully');
  } catch (error) {
    console.error('Firebase initialization error:', error);
  }
} else {
  console.warn('Firebase not configured - using localStorage fallback');
}

// ============================================
// SUBMISSIONS SERVICE
// ============================================

// Add a new submission
export const addSubmission = async (submission) => {
  if (!database) {
    // Fallback to localStorage
    const existing = JSON.parse(localStorage.getItem('rarecanvas_material_submissions') || '[]');
    existing.push(submission);
    localStorage.setItem('rarecanvas_material_submissions', JSON.stringify(existing));
    return submission.id;
  }

  try {
    const submissionsRef = ref(database, 'submissions');
    const newRef = push(submissionsRef);
    await set(newRef, {
      ...submission,
      firebaseKey: newRef.key
    });
    return newRef.key;
  } catch (error) {
    console.error('Error adding submission:', error);
    // Fallback to localStorage
    const existing = JSON.parse(localStorage.getItem('rarecanvas_material_submissions') || '[]');
    existing.push(submission);
    localStorage.setItem('rarecanvas_material_submissions', JSON.stringify(existing));
    return submission.id;
  }
};

// Get all submissions
export const getSubmissions = async () => {
  if (!database) {
    return JSON.parse(localStorage.getItem('rarecanvas_material_submissions') || '[]');
  }

  try {
    const submissionsRef = ref(database, 'submissions');
    const snapshot = await get(submissionsRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      return Object.values(data);
    }
    return [];
  } catch (error) {
    console.error('Error getting submissions:', error);
    return JSON.parse(localStorage.getItem('rarecanvas_material_submissions') || '[]');
  }
};

// Subscribe to submissions (real-time updates)
export const subscribeToSubmissions = (callback) => {
  if (!database) {
    // For localStorage, just call callback once with current data
    const data = JSON.parse(localStorage.getItem('rarecanvas_material_submissions') || '[]');
    callback(data);
    return () => {}; // No-op unsubscribe
  }

  const submissionsRef = ref(database, 'submissions');
  const unsubscribe = onValue(submissionsRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(Object.values(snapshot.val()));
    } else {
      callback([]);
    }
  });

  return unsubscribe;
};

// Update submission status (approve/reject)
export const updateSubmissionStatus = async (submissionId, status, additionalData = {}) => {
  if (!database) {
    const existing = JSON.parse(localStorage.getItem('rarecanvas_material_submissions') || '[]');
    const updated = existing.map(sub => {
      if (sub.id === submissionId || sub.firebaseKey === submissionId) {
        return { ...sub, status, ...additionalData };
      }
      return sub;
    });
    localStorage.setItem('rarecanvas_material_submissions', JSON.stringify(updated));
    return true;
  }

  try {
    // Find the submission by ID
    const submissionsRef = ref(database, 'submissions');
    const snapshot = await get(submissionsRef);

    if (snapshot.exists()) {
      const submissions = snapshot.val();
      for (const [key, sub] of Object.entries(submissions)) {
        if (sub.id === submissionId || key === submissionId) {
          const updateRef = ref(database, `submissions/${key}`);
          await update(updateRef, { status, ...additionalData });
          return true;
        }
      }
    }
    return false;
  } catch (error) {
    console.error('Error updating submission:', error);
    return false;
  }
};

// Delete submission
export const deleteSubmission = async (submissionId) => {
  if (!database) {
    const existing = JSON.parse(localStorage.getItem('rarecanvas_material_submissions') || '[]');
    const updated = existing.filter(sub => sub.id !== submissionId && sub.firebaseKey !== submissionId);
    localStorage.setItem('rarecanvas_material_submissions', JSON.stringify(updated));
    return true;
  }

  try {
    const submissionsRef = ref(database, 'submissions');
    const snapshot = await get(submissionsRef);

    if (snapshot.exists()) {
      const submissions = snapshot.val();
      for (const [key, sub] of Object.entries(submissions)) {
        if (sub.id === submissionId || key === submissionId) {
          const deleteRef = ref(database, `submissions/${key}`);
          await remove(deleteRef);
          return true;
        }
      }
    }
    return false;
  } catch (error) {
    console.error('Error deleting submission:', error);
    return false;
  }
};

// ============================================
// FEATURED COLLECTIONS SERVICE
// ============================================

export const addFeaturedCollection = async (collection) => {
  if (!database) {
    const existing = JSON.parse(localStorage.getItem('rarecanvas_featured_collections') || '[]');
    existing.push(collection);
    localStorage.setItem('rarecanvas_featured_collections', JSON.stringify(existing));
    return true;
  }

  try {
    const featuredRef = ref(database, 'featured_collections');
    const newRef = push(featuredRef);
    await set(newRef, collection);
    return true;
  } catch (error) {
    console.error('Error adding featured collection:', error);
    return false;
  }
};

export const getFeaturedCollections = async () => {
  if (!database) {
    return JSON.parse(localStorage.getItem('rarecanvas_featured_collections') || '[]');
  }

  try {
    const featuredRef = ref(database, 'featured_collections');
    const snapshot = await get(featuredRef);
    if (snapshot.exists()) {
      return Object.values(snapshot.val());
    }
    return [];
  } catch (error) {
    console.error('Error getting featured collections:', error);
    return [];
  }
};

// ============================================
// VERIFIED CREATORS SERVICE
// ============================================

export const addVerifiedCreator = async (creator) => {
  if (!database) {
    const existing = JSON.parse(localStorage.getItem('rarecanvas_verified_creators') || '[]');
    existing.push(creator);
    localStorage.setItem('rarecanvas_verified_creators', JSON.stringify(existing));
    return true;
  }

  try {
    const verifiedRef = ref(database, 'verified_creators');
    const newRef = push(verifiedRef);
    await set(newRef, creator);
    return true;
  } catch (error) {
    console.error('Error adding verified creator:', error);
    return false;
  }
};

export const getVerifiedCreators = async () => {
  if (!database) {
    return JSON.parse(localStorage.getItem('rarecanvas_verified_creators') || '[]');
  }

  try {
    const verifiedRef = ref(database, 'verified_creators');
    const snapshot = await get(verifiedRef);
    if (snapshot.exists()) {
      return Object.values(snapshot.val());
    }
    return [];
  } catch (error) {
    console.error('Error getting verified creators:', error);
    return [];
  }
};

export const isCreatorVerified = async (walletAddress) => {
  const creators = await getVerifiedCreators();
  return creators.some(c => c.wallet?.toLowerCase() === walletAddress?.toLowerCase());
};

// ============================================
// SERVICE PURCHASES SERVICE
// ============================================

export const addPurchase = async (purchase) => {
  if (!database) {
    const existing = JSON.parse(localStorage.getItem('rarecanvas_service_purchases') || '[]');
    existing.push(purchase);
    localStorage.setItem('rarecanvas_service_purchases', JSON.stringify(existing));
    return true;
  }

  try {
    const purchasesRef = ref(database, 'purchases');
    const newRef = push(purchasesRef);
    await set(newRef, purchase);
    return true;
  } catch (error) {
    console.error('Error adding purchase:', error);
    return false;
  }
};

export const getPurchases = async () => {
  if (!database) {
    return JSON.parse(localStorage.getItem('rarecanvas_service_purchases') || '[]');
  }

  try {
    const purchasesRef = ref(database, 'purchases');
    const snapshot = await get(purchasesRef);
    if (snapshot.exists()) {
      return Object.values(snapshot.val());
    }
    return [];
  } catch (error) {
    console.error('Error getting purchases:', error);
    return [];
  }
};

// Export database status checker
export const isUsingFirebase = () => !!database;

export { database, isFirebaseConfigured };
